/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useCallback, useEffect, useId, useRef, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { ErrorMessage } from '@atlaskit/form/messages';
import { RequiredAsterisk } from '@atlaskit/form/required-asterisk';
import Heading from '@atlaskit/heading';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import CrossIcon from '@atlaskit/icon/core/cross';
import PageIcon from '@atlaskit/icon/core/page';
import StatusVerifiedIcon from '@atlaskit/icon/core/status-verified';
import DislikeIcon from '@atlaskit/icon/core/thumbs-down';
import LikeIcon from '@atlaskit/icon/core/thumbs-up';
import Popup from '@atlaskit/popup';
import { type PopupProps } from '@atlaskit/popup/types';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { Card } from '@atlaskit/smart-card/card/lazy';
import {
	ElementName,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
} from '@atlaskit/smart-card/enums';
import { CustomBlock } from '@atlaskit/smart-card/flexible/custom-block';
import { TitleBlock } from '@atlaskit/smart-card/flexible/title-block';
import TextArea from '@atlaskit/textarea/text-area';
import { token } from '@atlaskit/tokens';
import type {
	AuthorityDomainExpert,
	AuthoritySource,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { type HydratedExpert, useFetchUserProfiles } from './hooks/use-fetch-user-profiles';
import messages from './messages';

/**
 * Hover-to-open + click-to-lock popup behaviour, matching the inline citation pattern.
 * - Hovering the trigger opens the popup after no delay.
 * - Moving the mouse off the trigger starts a 150ms close timer.
 * - Moving into the popup content cancels the timer (so you can interact with it).
 * - Clicking the trigger locks it open; click again or close button to dismiss.
 */
const useHoverPopup = (isOpen: boolean, open: () => void, close: () => void) => {
	const lockedByClick = useRef(false);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (closeTimer.current) clearTimeout(closeTimer.current);
		},
		[],
	);

	const closeAndUnlock = useCallback(() => {
		lockedByClick.current = false;
		close();
	}, [close]);

	const onTriggerMouseEnter = useCallback(() => {
		if (closeTimer.current) clearTimeout(closeTimer.current);
		open();
	}, [open]);
	const onTriggerMouseLeave = useCallback(() => {
		closeTimer.current = setTimeout(() => {
			if (!lockedByClick.current) closeAndUnlock();
		}, 150);
	}, [closeAndUnlock]);
	const onTriggerClick = useCallback(() => {
		if (isOpen && lockedByClick.current) {
			closeAndUnlock();
			return;
		}
		lockedByClick.current = !lockedByClick.current;
		open();
	}, [isOpen, open, closeAndUnlock]);
	const onContentMouseEnter = useCallback(() => {
		if (closeTimer.current) clearTimeout(closeTimer.current);
	}, []);
	const onContentMouseLeave = useCallback(() => {
		if (!lockedByClick.current) closeAndUnlock();
	}, [closeAndUnlock]);
	const onCloseButton = closeAndUnlock;

	return {
		onTriggerMouseEnter,
		onTriggerMouseLeave,
		onTriggerClick,
		onContentMouseEnter,
		onContentMouseLeave,
		onCloseButton,
	};
};

/**
 * Normalized feedback submission emitted by the shared panel. Each surface
 * (Chat, SAIN) maps this to its own backend payload and fills any
 * surface-specific fields with defaults.
 */
export type AuthorityFeedbackSubmission = {
	/** Whether the user rated the high confidence knowledge positively or negatively. */
	sentiment: 'like' | 'dislike';
	/** Selected reason labels (multi-select). Empty if none chosen. */
	reasons: string[];
	/** Optional free-text comment. */
	comment?: string;
};

export type AuthoritativeAnswerAttributionProps = {
	/** Trusted sources that contributed to the answer. */
	sources: AuthoritySource[];
	/** Domain experts associated with the answer's topic. */
	domainExperts: AuthorityDomainExpert[];
	/**
	 * Overall reasoning text from the backend — displayed at the top of the panel
	 * below the heading, when provided.
	 */
	overallReasoning?: string;
	/**
	 * Surface variant. `"generated"` (default) is for Chat — renders a standalone
	 * row with icon and "Verified across…" text. `"answer"` is for SAIN — renders
	 * only the attribution text to slot inline after the existing heading in the
	 * AICollapse title row.
	 */
	labelVariant?: 'generated' | 'answer';
	/**
	 * Invoked when a source row is activated. Surfaces inject their own
	 * navigation + analytics. The source's `url` may be `null`.
	 */
	onSourceClick?: (source: AuthoritySource) => void;
	/**
	 * Invoked when an expert row is activated. Surfaces inject their own
	 * navigation + analytics.
	 */
	onExpertClick?: (expert: AuthorityDomainExpert) => void;
	/**
	 * Fired when the detail panel is opened, for surface-level analytics.
	 * Intentionally does not differentiate between sources/experts — the panel
	 * is now combined. Analytics instrumentation (link clicked, panel open) is
	 * a planned follow-up after the redesign is finalised. Neither Chat nor SAIN
	 * currently pass this prop.
	 */
	onPanelOpen?: () => void;
	/**
	 * Cloud/tenant ID used to construct Atlassian Home profile URLs for expert contributors.
	 * When provided, experts with a valid accountId will show a profile link.
	 */
	cloudId?: string;
	/**
	 * Invoked when the user submits the in-panel feedback form. Each surface
	 * maps the normalized submission to its own feedback backend. When omitted,
	 * the form still renders but submission is a no-op (aside from the local
	 * confirmation state).
	 */
	onFeedbackSubmit?: (submission: AuthorityFeedbackSubmission) => void;
	/**
	 * The surface hosting the component, which determines how the detail popup
	 * anchors to its trigger:
	 * - `"chat"` (default): opens upward (`top-start`) and renders as a sibling of
	 *   the trigger, matching Chat's existing "N Sources" popup so it does not
	 *   cover the answer content in the narrow right panel.
	 * - `"sain"`: opens below the inline attribution text (`bottom-start`) and
	 *   portals to the body, which suits the roomy search results column.
	 *
	 * This controls only anchoring; use `panelSize` for the available vertical
	 * space (which is independent of surface).
	 */
	surface?: 'chat' | 'sain';
	/**
	 * Size of the detail popup panel. `"compact"` (default, max height 400px)
	 * matches Chat's "N Sources" popup for the narrow sidebar / mini-modal.
	 * `"tall"` relaxes the max height for surfaces with more vertical room (SAIN,
	 * full-page Chat) so the feedback form is not cramped behind a short scroll
	 * region.
	 */
	panelSize?: 'compact' | 'tall';
	testId?: string;
};

const styles = cssMap({
	root: {
		marginBlockStart: token('space.100'),
	},
	icon: {
		display: 'inline-flex',
		verticalAlign: 'middle',
		color: token('color.text.success'),
	},
	panelHeadingIcon: {
		color: token('color.text.success'),
		flexShrink: 0,
	},
	link: {
		display: 'inline',
		backgroundColor: 'transparent',
		paddingBlock: token('space.0'),
		paddingInline: token('space.0'),
		color: token('color.link'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		'&:hover': {
			color: token('color.link.pressed'),
			textDecoration: 'underline',
		},
	},
	panel: {
		// Compact variant — matches the Chat "N Sources" popup dimensions
		// (conversation-assistant-content-renderer SourcesPopupContent). Used in
		// Chat's narrow sidebar / mini-modal.
		width: '330px',
		maxWidth: '90vw',
		maxHeight: '400px',
		overflow: 'auto',
	},
	panelTall: {
		// Roomy variant — for surfaces with ample vertical space (SAIN results
		// column, full-page Chat). Slightly wider and taller than the compact
		// variant so the feedback form is not cramped.
		width: '360px',
		maxWidth: '90vw',
		maxHeight: 'min(600px, 80vh)',
		overflow: 'auto',
	},
	panelHeader: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.100'),
		paddingInline: token('space.200'),
	},
	panelDivider: {
		borderBlockStartWidth: token('border.width'),
		borderBlockStartStyle: 'solid',
		borderBlockStartColor: token('color.border'),
	},
	itemRow: {
		display: 'block',
		width: '100%',
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		backgroundColor: token('color.background.neutral.subtle'),
		textAlign: 'start',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	itemRowArrow: {
		flexShrink: 0,
		opacity: 0,
		transition: `opacity ${token('motion.duration.medium')}`,
	},
	itemRowArrowVisible: {
		flexShrink: 0,
		opacity: 1,
		transition: `opacity ${token('motion.duration.medium')}`,
	},
	feedbackRow: {
		paddingBlock: token('space.075'),
		paddingInline: token('space.200'),
	},
	feedbackForm: {
		marginBlock: token('space.150'),
		marginInline: token('space.200'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		// Leave breathing room when the form is scrolled into view via focus,
		// so its top edge is not flush against the panel's scroll boundary.
		scrollMarginBlock: token('space.150'),
		// This container is a programmatic focus target (not user-tabbable), so
		// suppress the focus ring that would otherwise appear around the whole card.
		'&:focus': {
			outline: 'none',
		},
		'&:focus-visible': {
			outline: 'none',
		},
	},
	textAreaWrap: {
		// Bordered wrapper around the borderless TextArea, matching Chat's feedback
		// form (FeedbackConfirmationBanner) so the field reads the same on both.
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.input'),
		paddingInline: token('space.050'),
		paddingBlock: token('space.050'),
		overflow: 'hidden',
		'&:focus-within': {
			borderColor: token('color.border.focused'),
			borderWidth: token('border.width.focused'),
		},
	},
});

const titleBlockCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	"div[data-smart-element='LinkIcon']": {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.neutral'),
		marginTop: token('space.negative.050'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'div[data-testid="smart-element-group"]': {
		gap: token('space.0'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	"[data-smart-element='Provider'], [data-smart-element='ModifiedOn']": {
		color: token('color.text.subtlest'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'div[data-smart-block="custom"]': {
		marginBlockStart: token('space.025'),
	},
});

const ExpertRow = ({
	expert,
	index,
	onExpertClick,
	cloudId,
	testId,
}: {
	expert: HydratedExpert;
	index: number;
	onExpertClick?: (expert: AuthorityDomainExpert) => void;
	cloudId?: string;
	testId?: string;
}) => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const profileUrl = expert.accountId
		? cloudId
			? `https://home.atlassian.com/people/${expert.accountId}?cloudId=${cloudId}`
			: `/people/${expert.accountId}`
		: undefined;

	const expertContent = (
		<Stack space="space.100">
			<Inline alignBlock="center" space="space.100">
				<Avatar size="medium" name={expert.resolvedName} src={expert.avatarUrl} />
				<Stack space="space.0" grow="fill">
					<Inline alignBlock="center" spread="space-between">
						<Stack space="space.0">
							<Heading as="h3" size="xxsmall">
								{expert.resolvedName}
							</Heading>
							{expert.jobTitle || expert.role ? (
								<Text color="color.text.subtlest" size="small">
									{expert.jobTitle || expert.role}
								</Text>
							) : null}
						</Stack>
						{profileUrl ? (
							<Box xcss={isHovered ? styles.itemRowArrowVisible : styles.itemRowArrow}>
								<IconButton
									icon={ArrowRightIcon}
									label={formatMessage(messages.viewExpert, { name: expert.resolvedName })}
									appearance="default"
									tabIndex={isHovered ? 0 : -1}
								/>
							</Box>
						) : null}
					</Inline>
				</Stack>
			</Inline>
			{expert.reasoning ? (
				<Text color="color.text.subtlest" size="small" maxLines={2}>
					{expert.reasoning}
				</Text>
			) : null}
		</Stack>
	);

	return profileUrl ? (
		<Pressable
			xcss={styles.itemRow}
			onClick={() => {
				onExpertClick?.(expert);
				window.open(profileUrl, '_blank', 'noopener,noreferrer');
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			testId={testId ? `${testId}--expert-${index}` : undefined}
		>
			{expertContent}
		</Pressable>
	) : (
		<Box xcss={styles.itemRow} testId={testId ? `${testId}--expert-${index}` : undefined}>
			{expertContent}
		</Box>
	);
};

const SourceRow = ({
	source,
	index,
	onSourceClick,
	onError,
	testId,
}: {
	source: AuthoritySource;
	index: number;
	onSourceClick?: (source: AuthoritySource) => void;
	onError?: (url: string) => void;
	testId?: string;
}) => {
	return source.url ? (
		<Box xcss={styles.itemRow} testId={testId ? `${testId}--source-${index}` : undefined}>
			<Card
				appearance="block"
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideBackground: true,
					hideElevation: true,
					hidePadding: true,
					size: SmartLinkSize.Medium,
					removeBlockRestriction: true,
				}}
				url={source.url}
				onClick={() => onSourceClick?.(source)}
				onError={() => source.url && onError?.(source.url)}
			>
				<TitleBlock
					maxLines={1}
					size={SmartLinkSize.Medium}
					position={SmartLinkPosition.Center}
					subtitle={[
						{ name: ElementName.Provider, hideIcon: true },
						{ name: ElementName.ModifiedOn },
					]}
					css={titleBlockCss}
				/>
				{source.reasoning ? (
					<CustomBlock>
						<Text color="color.text.subtlest" size="small" maxLines={2}>
							{source.reasoning}
						</Text>
					</CustomBlock>
				) : null}
			</Card>
		</Box>
	) : (
		<Box xcss={styles.itemRow} testId={testId ? `${testId}--source-${index}` : undefined}>
			<Stack space="space.050">
				<Inline alignBlock="center" space="space.100">
					<PageIcon label="" size="medium" />
					<Text weight="medium" maxLines={1}>
						{source.title}
					</Text>
				</Inline>
				{source.reasoning ? (
					<Text color="color.text.subtlest" size="small" maxLines={2}>
						{source.reasoning}
					</Text>
				) : null}
			</Stack>
		</Box>
	);
};

const MAX_PANEL_ITEMS = 3;
const MAX_SOURCES = 2;

const sliceItems = <S, E>(sources: S[], experts: E[]): { sources: S[]; experts: E[] } => {
	const sourcesCount = Math.min(sources.length, experts.length > 0 ? MAX_SOURCES : MAX_PANEL_ITEMS);
	const expertsCount = Math.min(experts.length, MAX_PANEL_ITEMS - sourcesCount);
	return {
		sources: sources.slice(0, sourcesCount),
		experts: experts.slice(0, expertsCount),
	};
};

const CombinedPanel = ({
	sources,
	domainExperts,
	overallReasoning,
	onSourceClick,
	onExpertClick,
	onClose,
	onFeedbackSubmit,
	cloudId,
	panelId,
	panelSize,
	testId,
}: {
	sources: AuthoritySource[];
	domainExperts: HydratedExpert[];
	overallReasoning?: string;
	onSourceClick?: (source: AuthoritySource) => void;
	onExpertClick?: (expert: AuthorityDomainExpert) => void;
	onClose: () => void;
	onFeedbackSubmit?: (submission: AuthorityFeedbackSubmission) => void;
	cloudId?: string;
	panelId: string;
	panelSize?: 'compact' | 'tall';
	testId?: string;
}) => {
	const { formatMessage } = useIntl();
	const [erroredUrls, setErroredUrls] = useState<Set<string>>(new Set());
	const [feedbackSentiment, setFeedbackSentiment] = useState<'like' | 'dislike' | null>(null);
	const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
	const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
	const [feedbackComment, setFeedbackComment] = useState('');
	const [showValidationError, setShowValidationError] = useState(false);

	// Move focus to the revealed form so it scrolls into view and is announced.
	// `tabIndex` is set imperatively because the compiled `Stack` does not accept
	// a `tabIndex` prop; -1 keeps it out of the tab order but allows `.focus()`.
	const feedbackFormRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const node = feedbackFormRef.current;
		if (feedbackSentiment && node) {
			node.tabIndex = -1;
			node.focus();
		}
	}, [feedbackSentiment]);

	const toggleReason = useCallback((reason: string) => {
		setSelectedReasons((prev) =>
			prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason],
		);
	}, []);

	const positiveReasons = [
		formatMessage(messages.categoryUpToDate),
		formatMessage(messages.categoryAccurate),
		formatMessage(messages.categoryHelpful),
		formatMessage(messages.categoryWellSourced),
		formatMessage(messages.categoryOther),
	];
	const negativeReasons = [
		formatMessage(messages.categoryOutOfDate),
		formatMessage(messages.categoryInaccurate),
		formatMessage(messages.categoryNotHelpful),
		formatMessage(messages.categoryPoorlySourced),
		formatMessage(messages.categoryOther),
	];
	const otherLabel = formatMessage(messages.categoryOther);
	const feedbackReasons = feedbackSentiment === 'like' ? positiveReasons : negativeReasons;
	const isOtherSelected = selectedReasons.includes(otherLabel);
	const showCommentError = showValidationError && isOtherSelected && !feedbackComment.trim();

	const handleFeedbackSubmit = () => {
		if (!feedbackSentiment) {
			return;
		}
		// Free-text is required when the "Other" reason is selected.
		if (isOtherSelected && !feedbackComment.trim()) {
			setShowValidationError(true);
			return;
		}
		onFeedbackSubmit?.({
			sentiment: feedbackSentiment,
			reasons: selectedReasons,
			comment: feedbackComment || undefined,
		});
		setFeedbackSubmitted(true);
	};

	const handleSourceError = useCallback(
		(url: string) => setErroredUrls((prev) => new Set([...prev, url])),
		[],
	);

	// Filter errored sources before slicing so we always show up to the cap from
	// valid sources (Smart Card resolution errors arrive async via onError).
	const validSources = sources.filter((s) => !s.url || !erroredUrls.has(s.url));
	const { sources: slicedSources, experts: slicedExperts } = sliceItems(
		validSources,
		domainExperts,
	);

	return (
		<Box id={panelId} xcss={panelSize === 'tall' ? styles.panelTall : styles.panel} testId={testId}>
			{/* Panel header */}
			<Stack xcss={styles.panelHeader} space="space.0">
				<Inline spread="space-between" alignBlock="center">
					<Inline alignBlock="center" space="space.075">
						<Box xcss={styles.panelHeadingIcon}>
							<StatusVerifiedIcon label="" size="small" />
						</Box>
						<Heading as="h2" size="xsmall">
							{formatMessage(messages.panelHeading)}
						</Heading>
					</Inline>
					<IconButton
						icon={CrossIcon}
						label={formatMessage(messages.closePanel)}
						appearance="subtle"
						onClick={onClose}
						testId={testId ? `${testId}--close` : undefined}
					/>
				</Inline>
				{overallReasoning ? (
					<Text color="color.text.subtlest" size="small">
						{overallReasoning}
					</Text>
				) : null}
			</Stack>

			<Box xcss={styles.panelDivider} />

			{/* Sources section */}
			{slicedSources.length > 0 ? (
				<Stack space="space.0">
					{slicedSources.map((source, index) => (
						<SourceRow
							key={`${source.title}-${index}`}
							source={source}
							index={index}
							onSourceClick={onSourceClick}
							onError={handleSourceError}
							testId={testId}
						/>
					))}
				</Stack>
			) : null}

			{/* Contributors section */}
			{slicedExperts.length > 0 ? (
				<Fragment>
					{slicedSources.length > 0 ? <Box xcss={styles.panelDivider} /> : null}
					<Stack space="space.0">
						{slicedExperts.map((expert, index) => (
							<ExpertRow
								key={`${expert.identifier}-${index}`}
								expert={expert}
								index={index}
								onExpertClick={onExpertClick}
								cloudId={cloudId}
								testId={testId}
							/>
						))}
					</Stack>
				</Fragment>
			) : null}

			{/* Feedback */}
			<Box xcss={styles.panelDivider} />
			{feedbackSubmitted ? (
				<Box
					xcss={styles.feedbackRow}
					testId={testId ? `${testId}--feedback-confirmation` : undefined}
				>
					<Text color="color.text.subtlest" size="small">
						{formatMessage(messages.feedbackConfirmation)}
					</Text>
				</Box>
			) : (
				<Fragment>
					{/* Prompt row with persistent, selectable thumbs. */}
					<Inline
						xcss={styles.feedbackRow}
						spread="space-between"
						alignBlock="center"
						space="space.100"
					>
						<Text color="color.text.subtlest" size="small">
							{formatMessage(messages.feedbackPrompt)}
						</Text>
						<Inline space="space.050" alignBlock="center">
							<IconButton
								icon={LikeIcon}
								label={formatMessage(messages.feedbackHelpfulLabel)}
								appearance="subtle"
								spacing="compact"
								isSelected={feedbackSentiment === 'like'}
								onClick={() => setFeedbackSentiment('like')}
								testId={testId ? `${testId}--feedback-helpful` : undefined}
							/>
							<IconButton
								icon={DislikeIcon}
								label={formatMessage(messages.feedbackNotHelpfulLabel)}
								appearance="subtle"
								spacing="compact"
								isSelected={feedbackSentiment === 'dislike'}
								onClick={() => setFeedbackSentiment('dislike')}
								testId={testId ? `${testId}--feedback-not-helpful` : undefined}
							/>
						</Inline>
					</Inline>
					{/* Bordered form card, shown below the prompt after a thumb is chosen. */}
					{feedbackSentiment ? (
						<Stack
							ref={feedbackFormRef}
							xcss={styles.feedbackForm}
							space="space.100"
							testId={testId ? `${testId}--feedback-form` : undefined}
						>
							<Inline spread="space-between" alignBlock="center">
								<Heading as="h3" size="xxsmall">
									{feedbackSentiment === 'like'
										? formatMessage(messages.feedbackHeadingPositive)
										: formatMessage(messages.feedbackHeadingNegative)}
								</Heading>
								<IconButton
									icon={CrossIcon}
									label={formatMessage(messages.feedbackCloseLabel)}
									appearance="subtle"
									spacing="compact"
									onClick={() => setFeedbackSentiment(null)}
									testId={testId ? `${testId}--feedback-form-close` : undefined}
								/>
							</Inline>
							{/* Subtle caption introducing the reason chips, matching Chat. */}
							<Text size="small" color="color.text.subtlest" weight="bold">
								{formatMessage(messages.feedbackItWasLabel)}
							</Text>
							<Inline
								space="space.100"
								shouldWrap
								role="group"
								aria-label={formatMessage(messages.feedbackItWasLabel)}
							>
								{feedbackReasons.map((reason) => (
									<Button
										key={reason}
										isSelected={selectedReasons.includes(reason)}
										onClick={() => toggleReason(reason)}
										testId={testId ? `${testId}--feedback-reason` : undefined}
									>
										{reason}
									</Button>
								))}
							</Inline>
							{isOtherSelected ? (
								<Stack space="space.100">
									<Text size="small" color="color.text.subtlest" weight="bold">
										{formatMessage(messages.feedbackAdditionalLabel)} <RequiredAsterisk />
									</Text>
									<Box xcss={styles.textAreaWrap}>
										<TextArea
											appearance="none"
											value={feedbackComment}
											onChange={(e) => setFeedbackComment(e.currentTarget.value)}
											placeholder={formatMessage(messages.feedbackAdditionalPlaceholder)}
											minimumRows={3}
											resize="vertical"
											aria-required
											aria-invalid={showCommentError}
											testId={testId ? `${testId}--feedback-comment` : undefined}
										/>
									</Box>
									{showCommentError ? (
										<ErrorMessage>
											{formatMessage(messages.feedbackAdditionalRequired)}
										</ErrorMessage>
									) : null}
								</Stack>
							) : null}
							<Button
								appearance="primary"
								onClick={handleFeedbackSubmit}
								testId={testId ? `${testId}--feedback-submit` : undefined}
							>
								{formatMessage(messages.feedbackSubmitLabel)}
							</Button>
						</Stack>
					) : null}
				</Fragment>
			)}
		</Box>
	);
};

/**
 * Attribution line shown above an authoritative AI answer.
 *
 * Renders "Answer verified across high confidence knowledge" (SAIN) or
 * "Verified across high confidence knowledge" (Chat) where the bolded text
 * opens a combined panel showing sources and expert contributors.
 *
 * Designed for reuse across Rovo surfaces (Rovo Chat, SAIN).
 */
export const AuthoritativeAnswerAttribution = ({
	sources,
	domainExperts,
	overallReasoning,
	labelVariant = 'generated',
	onSourceClick,
	onExpertClick,
	onPanelOpen,
	cloudId,
	onFeedbackSubmit,
	surface = 'chat',
	panelSize = 'compact',
	testId,
}: AuthoritativeAnswerAttributionProps): JSX.Element | null => {
	// Anchoring is derived from the surface: Chat opens upward as a sibling of the
	// trigger (matching its "N Sources" popup so the answer stays visible in the
	// narrow right panel); SAIN opens below the inline text and portals to body.
	const placement: PopupProps['placement'] = surface === 'sain' ? 'bottom-start' : 'top-start';
	const shouldRenderToParent = surface === 'chat';
	const instanceId = useId();
	const panelId = `${instanceId}-panel`;
	const [isOpen, setIsOpen] = useState(false);

	const { experts: allHydratedExperts } = useFetchUserProfiles(domainExperts);

	// Filter out experts with unresolvable identifiers (accountId: null)
	const hydratedExperts = allHydratedExperts.filter((e) => e.accountId !== null);

	const hasSources = sources.length > 0;
	const hasExperts = hydratedExperts.length > 0;

	const openPanel = useCallback(() => {
		// Fire onPanelOpen only on the closed -> open transition so hover + click
		// re-entrancy does not double-count the panel-shown analytics event.
		setIsOpen((wasOpen) => {
			if (!wasOpen) {
				onPanelOpen?.();
			}
			return true;
		});
	}, [onPanelOpen]);
	const closePanel = useCallback(() => setIsOpen(false), []);

	const isSAINVariant = labelVariant === 'answer';
	const hoverPopup = useHoverPopup(isOpen, openPanel, closePanel);

	if (!hasSources && !hasExperts) {
		return null;
	}

	// Rendered as an ICU tag so the full attribution sentence — including the
	// linked phrase — lives in a single translatable message. `chunks` is the
	// translated link text supplied by the surrounding message.
	const trigger = (chunks: React.ReactNode) => (
		<Popup
			isOpen={isOpen}
			onClose={hoverPopup.onCloseButton}
			placement={placement}
			shouldRenderToParent={shouldRenderToParent}
			content={() => (
				<Box
					onMouseEnter={hoverPopup.onContentMouseEnter}
					onMouseLeave={hoverPopup.onContentMouseLeave}
				>
					<CombinedPanel
						sources={sources}
						domainExperts={hydratedExperts}
						overallReasoning={overallReasoning}
						onSourceClick={onSourceClick}
						onExpertClick={onExpertClick}
						onClose={hoverPopup.onCloseButton}
						onFeedbackSubmit={onFeedbackSubmit}
						cloudId={cloudId}
						panelId={panelId}
						panelSize={panelSize}
						testId={testId ? `${testId}--panel` : undefined}
					/>
				</Box>
			)}
			trigger={(triggerProps) => (
				<Box
					as="span"
					role="button"
					tabIndex={0}
					{...triggerProps}
					aria-expanded={isOpen}
					aria-controls={panelId}
					xcss={styles.link}
					onClick={hoverPopup.onTriggerClick}
					onKeyDown={(e: React.KeyboardEvent) => {
						if (e.key === 'Enter' || e.key === ' ') hoverPopup.onTriggerClick();
					}}
					onMouseEnter={isSAINVariant ? hoverPopup.onTriggerMouseEnter : undefined}
					onMouseLeave={isSAINVariant ? hoverPopup.onTriggerMouseLeave : undefined}
					testId={testId ? `${testId}--trigger` : undefined}
				>
					{chunks}
				</Box>
			)}
		/>
	);

	// SAIN: render only the attribution text inline
	if (isSAINVariant) {
		return (
			<Text color="color.text.subtlest" testId={testId}>
				<FormattedMessage
					{...messages.attributionAnswer}
					values={{
						trigger,
						bold: (chunks: React.ReactNode) => (
							<Text as="strong" color="color.text" weight="bold">
								{chunks}
							</Text>
						),
					}}
				/>
			</Text>
		);
	}

	return (
		<Box xcss={styles.root} testId={testId}>
			<Text color="color.text.subtlest">
				<Box as="span" xcss={styles.icon}>
					<StatusVerifiedIcon label="" size="small" />
				</Box>{' '}
				<FormattedMessage {...messages.attributionGenerated} values={{ trigger }} />
			</Text>
		</Box>
	);
};
